import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';

export interface CountdownParts {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

export type CountdownVariant = 'banner' | 'light' | 'notice';

/**
 * Four-tile countdown used on the portal gate.
 * Emits `expired` once when the remaining time reaches zero.
 */
@Component({
  selector: 'app-gate-countdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="w-full" role="timer" [attr.aria-label]="label()">
      <div class="grid grid-cols-4 gap-2 sm:gap-3">
        @for (unit of units(); track unit.key) {
          <div [class]="tileClass()">
            <p [class]="valueClass()">{{ parts()[unit.key] }}</p>
            <p [class]="unitClass()">{{ unit.label }}</p>
          </div>
        }
      </div>
    </div>
  `,
})
export class GateCountdownComponent implements OnInit, OnDestroy {
  readonly endsAt = input<string | null>(null);
  readonly variant = input<CountdownVariant>('banner');
  readonly expired = output<void>();

  readonly parts = signal<CountdownParts>({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
  });
  readonly label = signal('Time remaining');

  private timer: ReturnType<typeof setInterval> | null = null;
  private emitted = false;

  readonly units = computed(() => {
    const short = this.variant() !== 'light';
    return [
      { key: 'days' as const, label: 'Days' },
      { key: 'hours' as const, label: 'Hours' },
      { key: 'minutes' as const, label: short ? 'Mins' : 'Minutes' },
      { key: 'seconds' as const, label: short ? 'Secs' : 'Seconds' },
    ];
  });

  readonly tileClass = computed(() => {
    switch (this.variant()) {
      case 'light':
        return 'rounded-xl bg-white shadow-sm ring-1 ring-black/5 px-1.5 py-3 sm:px-3 sm:py-4 text-center';
      case 'notice':
        return 'rounded-md bg-black/20 ring-1 ring-white/10 px-1.5 py-3 sm:px-2 sm:py-4 text-center';
      default:
        return 'rounded-lg bg-[#063D26] ring-1 ring-[#4CA06C]/55 px-1.5 py-2.5 sm:px-2.5 sm:py-3 text-center';
    }
  });

  readonly valueClass = computed(() => {
    switch (this.variant()) {
      case 'light':
        return 'text-2xl sm:text-4xl font-extrabold tabular-nums tracking-tight text-forest-500';
      case 'notice':
        return 'text-2xl sm:text-[2rem] font-semibold tabular-nums tracking-tight text-white leading-none';
      default:
        return 'text-xl sm:text-2xl font-extrabold tabular-nums tracking-tight text-white leading-none';
    }
  });

  readonly unitClass = computed(() => {
    switch (this.variant()) {
      case 'light':
        return 'text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 mt-1 font-semibold';
      case 'notice':
        return 'text-[9px] uppercase tracking-[0.18em] text-white/55 mt-2';
      default:
        return 'text-[9px] sm:text-[10px] uppercase tracking-[0.14em] text-[#8FCBAA] mt-1.5';
    }
  });

  constructor() {
    effect(() => {
      this.endsAt();
      this.emitted = false;
      this.tick();
    });
  }

  ngOnInit(): void {
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private tick(): void {
    const raw = this.endsAt();
    if (!raw) {
      this.parts.set({ days: '00', hours: '00', minutes: '00', seconds: '00' });
      this.label.set('Countdown not scheduled');
      return;
    }

    const end = Date.parse(raw);
    if (Number.isNaN(end)) {
      return;
    }

    const remaining = Math.max(0, end - Date.now());
    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.parts.set({
      days: days.toString().padStart(2, '0'),
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0'),
    });
    this.label.set(
      remaining <= 0
        ? 'The scheduled window has ended'
        : `Launching in ${days} days ${hours} hours ${minutes} minutes ${seconds} seconds`
    );

    if (remaining <= 0 && !this.emitted) {
      this.emitted = true;
      this.expired.emit();
    }
  }
}
